/**
 * Product Card Management System - Moon Moon Agency v1.0
 * Handles variant selection and quantity controls for product cards
 * Uses event delegation pattern consistent with mm_main_product.js
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION
 */
const CARD_CONFIG = {
    selectors: {
        cardForm: '[data-type="add-to-cart-form"]',
        variantInput: '[data-card-variant-input]',
        variantIdInput: '[data-variant-id-input]',
        quantityInput: '[data-card-qtty-input]',
        quantityIncrease: '[data-card-qtty-increase]',
        quantityDecrease: '[data-card-qtty-decrease]',
        submitButton: '.mm-product-cta',
        productCard: '.mm-product-card',
        toggleOverlay: '[data-toggle-overlay]',
        overlay: '.mm-product-card-overlay',
        pcardMedia: '[data-pcard-images]',
    },

};

/*********************************************************************************************
 * 📦 PRODUCT DATA MANAGEMENT
 */
const ProductCardData = {
    /**
     * Get product data from a specific card element
     * @param {HTMLElement} card - The card element containing the product data
     */
    getFromCard(card) {
        if (!card) {
            window.log('error', 'ProductCard', 'No card element provided');
            return null;
        }

        // Find the script tag within this specific card
        const scriptTag = card.querySelector('script[data-product-id]');
        if (!scriptTag) {
            window.log('error', 'ProductCard', 'No product data script found in card');
            return null;
        }

        try {
            const data = JSON.parse(scriptTag.textContent);

            // Validate required properties
            if (!data || typeof data.id === 'undefined' || !Array.isArray(data.variants)) {
                window.log('error', 'ProductCard', 'Invalid product data structure', { data });
                return null;
            }

            window.log('info', 'ProductCard', 'Product data loaded', {
                productId: data.id,
                variantsCount: data.variants.length
            });
            return data;
        } catch (e) {
            window.log('error', 'ProductCard', 'Failed to parse product data', { error: e.message });
            return null;
        }
    }
};

/*********************************************************************************************
 * 🔄 VARIANT MANAGEMENT
 */
const VariantManager = {
    findMatchingVariant(productData, selectedOptions) {
        if (!productData || !productData.variants) {
            window.log('error', 'ProductCard', 'Invalid product data');
            return null;
        }

        const matchedVariant = productData.variants.find(variant => {
            // Check each option position
            return Object.keys(selectedOptions).every(optionKey => {
                const selectedValue = selectedOptions[optionKey];
                const variantValue = variant[optionKey];
                return variantValue === selectedValue;
            });
        });

        if (!matchedVariant) {
            window.log('warn', 'ProductCard', 'No variant matched', {
                selectedOptions,
                availableVariants: productData.variants.map(v => ({
                    option1: v.option1,
                    option2: v.option2,
                    option3: v.option3
                }))
            });
        }

        return matchedVariant;
    },

    getSelectedOptions(form) {
        const options = {};
        const inputs = form.querySelectorAll(CARD_CONFIG.selectors.variantInput);

        window.log('info', 'ProductCard', 'Getting selected options', {
            totalInputs: inputs.length,
            formId: form.getAttribute('id')
        });

        inputs.forEach(input => {
            if (input.checked) {
                const position = input.dataset.optionPosition;
                const optionKey = `option${position}`;
                options[optionKey] = input.value;

                window.log('info', 'ProductCard', 'Found checked option', {
                    position,
                    optionKey,
                    value: input.value
                });
            }
        });

        return options;
    },

    updateVariantId(form, variantId) {
        const input = form.querySelector(CARD_CONFIG.selectors.variantIdInput);
        if (input) {
            const oldValue = input.value;
            input.value = variantId;
            window.log('info', 'ProductCard', 'Variant ID updated in hidden input', {
                oldValue,
                newValue: variantId,
                inputName: input.name,
                inputId: input.id
            });
        } else {
            window.log('error', 'ProductCard', 'Hidden variant input not found', {
                selector: CARD_CONFIG.selectors.variantIdInput
            });
        }
    },

    updateButton(form, variant) {
        const button = form.querySelector(CARD_CONFIG.selectors.submitButton);
        if (!button) return;

        // Use texts from data attributes (set by Liquid translations)
        const addText = button.dataset.addText;
        const soldOutText = button.dataset.soldOutText;

        if (!addText || !soldOutText) {
            window.log('warn', 'ProductCard', 'Button missing translation data attributes', {
                hasAddText: !!addText,
                hasSoldOutText: !!soldOutText
            });
            return;
        }

        if (variant.available) {
            button.disabled = false;
            button.classList.remove('mm-cursor-default');
            button.textContent = addText;
        } else {
            button.disabled = true;
            button.classList.add('mm-cursor-default');
            button.textContent = soldOutText;
        }
    },

    updateVariantImage(form, variant) {
        if (!variant.featured_image) {
            window.log('info', 'ProductCard', 'No featured image for variant', { variantId: variant.id });
            return;
        }

        const card = form.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        // Find the main image in the card (not the hover image)
        const mainImage = card.querySelector('.mm-product-card-image:not(.mm-product-card-image-hover)');
        if (!mainImage) {
            window.log('warn', 'ProductCard', 'Main product image not found in card');
            return;
        }

        try {
            // featured_image is a URL string from the JSON data
            const imageUrl = typeof variant.featured_image === 'string'
                ? variant.featured_image
                : variant.featured_image.src;
            mainImage.src = imageUrl;
            mainImage.alt = variant.name || mainImage.alt;

            // Update srcset if present (for responsive images)
            if (mainImage.hasAttribute('srcset')) {
                // Generate responsive srcset for variant image
                const srcset = this.generateResponsiveSrcset(imageUrl);
                mainImage.srcset = srcset;
            }

            window.log('info', 'ProductCard', 'Variant image updated', {
                variantId: variant.id,
                imageUrl
            });
        } catch (error) {
            window.log('error', 'ProductCard', 'Failed to update variant image', {
                variantId: variant.id,
                error: error.message
            });
        }
    },

    generateResponsiveSrcset(originalUrl) {
        if (!originalUrl || typeof originalUrl !== 'string') {
            return '';
        }

        // Common responsive breakpoints
        const breakpoints = [200, 300, 400, 500, 600, 700, 800, 900, 1000];

        return breakpoints
            .map(width => `${this.getShopifyImageUrl(originalUrl, width)} ${width}w`)
            .join(', ');
    },

    getShopifyImageUrl(originalUrl, width) {
        if (!originalUrl || typeof originalUrl !== 'string') {
            return originalUrl;
        }

        try {
            // Remove any existing size parameters
            let cleanUrl = originalUrl.replace(/_\d+x\d*\./g, '.');

            // Handle URLs with query parameters (like v= version)
            const [baseUrl, queryString] = cleanUrl.split('?');

            // Add width parameter before file extension
            const sizedUrl = baseUrl.replace(/(\.[^.]*$)/, `_${width}x$1`);

            // Reattach query string if it exists
            return queryString ? `${sizedUrl}?${queryString}` : sizedUrl;
        } catch (error) {
            window.log('warn', 'ProductCard', 'Failed to resize image URL, using original', {
                originalUrl,
                error: error.message
            });
            return originalUrl;
        }
    },

    updateQuantityMax(form, variant) {
        const card = form.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        const quantityInput = card.querySelector(CARD_CONFIG.selectors.quantityInput);
        if (!quantityInput) return;

        let maxQty = 9999;

        if (variant.inventory_management &&
            variant.inventory_policy !== 'continue' &&
            variant.inventory_quantity > 0) {
            maxQty = variant.inventory_quantity;
        }

        quantityInput.max = maxQty;
        quantityInput.dataset.variantInventory = maxQty;

        // Reset quantity if it exceeds max
        const currentQty = parseInt(quantityInput.value, 10);
        if (currentQty > maxQty) {
            quantityInput.value = maxQty;
        }

        // Update button states
        QuantityManager.updateButtonStates(quantityInput);
    },

    handleVariantChange(form) {
        // Find the card containing this form
        const card = form.closest(CARD_CONFIG.selectors.productCard);
        if (!card) {
            window.log('error', 'ProductCard', 'Cannot find product card for form');
            return;
        }

        // Get product data from this specific card
        const productData = ProductCardData.getFromCard(card);
        if (!productData) {
            window.log('warn', 'ProductCard', 'No product data for variant change');
            return;
        }

        const selectedOptions = this.getSelectedOptions(form);
        const matchedVariant = this.findMatchingVariant(productData, selectedOptions);

        if (matchedVariant) {
            this.updateVariantId(form, matchedVariant.id);
            this.updateButton(form, matchedVariant);
            this.updateQuantityMax(form, matchedVariant);
            this.updateVariantImage(form, matchedVariant);

            window.log('info', 'ProductCard', 'Variant changed successfully', {
                productId: productData.id,
                variantId: matchedVariant.id,
                available: matchedVariant.available,
                selectedOptions
            });
        } else {
            window.log('warn', 'ProductCard', 'No variant matched', {
                productId: productData.id,
                selectedOptions,
                availableVariantsCount: productData.variants.length
            });

            // Disable button if no variant matches
            this.updateButton(form, { available: false });
        }
    }
};

/*********************************************************************************************
 * 🔢 QUANTITY MANAGEMENT
 */
const QuantityManager = {
    updateButtonStates(input) {
        if (!input) return;

        const currentValue = parseInt(input.value, 10) || 1;
        const maxValue = parseInt(input.max, 10) || 9999;

        const card = input.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        // Find plus button within the same card
        const plusBtn = card.querySelector(CARD_CONFIG.selectors.quantityIncrease);

        // Update plus button state only (minus button always enabled)
        if (plusBtn) {
            if (currentValue >= maxValue) {
                plusBtn.setAttribute('disabled', '');
            } else {
                plusBtn.removeAttribute('disabled');
            }
        }
    },

    changeQuantity(delta, button) {
        // Find the card container first, then find input within that card
        const card = button.closest(CARD_CONFIG.selectors.productCard);
        if (!card) {
            window.log('warn', 'ProductCard', 'Product card not found', { button });
            return;
        }

        const input = card.querySelector(CARD_CONFIG.selectors.quantityInput);
        if (!input) {
            window.log('warn', 'ProductCard', 'Quantity input not found in card', { card });
            return;
        }

        const current = parseInt(input.value, 10) || 1;
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 9999;

        // Calculate new value with constraints (same logic as mm_main_product.js)
        const newValue = Math.max(min, Math.min(max, current + delta));

        if (newValue !== current) {
            input.value = newValue;

            // Dispatch change event for potential listeners (consistent with mm_main_product.js)
            input.dispatchEvent(new Event('change', { bubbles: true }));

            this.updateButtonStates(input);

            window.log('info', 'ProductCard', 'Quantity changed', {
                oldValue: current,
                newValue,
                delta
            });
        }
    },

    validateInput(input) {
        if (!input) return;

        let value = parseInt(input.value, 10);
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 9999;

        if (isNaN(value) || value < min) {
            value = min;
        } else if (value > max) {
            value = max;
        }

        input.value = value;
        this.updateButtonStates(input);
    }
};

/*********************************************************************************************
 * 🎨 CARD SWATCH MANAGER
 */
const CardSwatchManager = {
    // scope : liste de cards neuves (passée par ProductCardManager.init). À défaut,
    // toutes les cards (1er appel). Évite de re-binder les hovers sur des cards déjà
    // câblées (sinon listeners empilés à chaque load-more).
    /* mouseenter par pastille : crossfade vers l'image si elle en a une, sinon
       afficher le message no-preview (couleur sans visuel). Pas de mouseleave par
       pastille → passer d'une pastille à l'autre fait un crossfade A→B propre.
       Extrait pour binder aussi les pastilles créées dynamiquement (top-6 global >250). */
    bindSwatch(swatch, media, noPreview) {
        // data-swatch-img est lu À CHAQUE événement, jamais capturé au binding : les pastilles
        // injectées par le filtre couleur et celles reconstruites en top-6 global (>250 variants)
        // reçoivent/changent leur image APRÈS l'attachement du listener. Une valeur figée laissait
        // la card sans message alors que la couleur survolée n'a pas de visuel.
        const apply = () => {
            const img = swatch.dataset.swatchImg;
            if (img) {
                PcardImageNav.previewInHover(media, img);
            } else {
                PcardImageNav.clearPreview(media);
            }
            if (noPreview) noPreview.classList.toggle('mm-pcard__no-preview--visible', !img);
        };
        swatch.addEventListener('mouseenter', apply, { passive: true });
        // Tactile : pointerenter/mouseenter ne partent pas de façon fiable au tap (cf. tooltip
        // couleur iOS) → sans pointerdown, le message n'apparaissait jamais sur mobile.
        swatch.addEventListener('pointerdown', apply, { passive: true });
    },

    setup(scope) {
        const cards = scope || document.querySelectorAll('.mm-pcard');
        cards.forEach((pcard) => {
            const swatches = pcard.querySelector('.mm-pcard__swatches');
            if (!swatches) return;
            const media = pcard.querySelector('.mm-pcard__media');
            if (!media) return;

            const noPreview = pcard.querySelector('.mm-pcard__no-preview');

            swatches.querySelectorAll('.mm-pcard__swatch').forEach((swatch) => {
                this.bindSwatch(swatch, media, noPreview);
            });

            /* En quittant la zone pastilles : retour à l'image par défaut et restauration du
               message selon la couleur SÉLECTIONNÉE (une couleur sans image cliquée garde son message). */
            swatches.addEventListener('mouseleave', () => {
                PcardImageNav.clearPreview(media);
                if (noPreview) {
                    const active = swatches.querySelector('.mm-pcard__swatch--active');
                    const activeHasImg = active ? !!active.dataset.swatchImg : true;
                    noPreview.classList.toggle('mm-pcard__no-preview--visible', !!active && !activeHasImg);
                }
            }, { passive: true });
        });
    }
};

/*********************************************************************************************
 * 🎨 LAZY SWATCH MANAGER
 * Pastilles chargées à l'entrée de la card dans le viewport — plus AUCUN rendu couleurs en
 * SSR grille : mm-pcard-colors-data parcourait product.variants + metaobjects par card, ce
 * qui dépassait le budget de rendu Liquid (~3,1 s → 500, prod comprise) sur les collections
 * riches en variantes. Fetch l'endpoint MM-Pcard-Colors (limit 14, déjà utilisé par le color
 * panel), injecte les JSON .mm-pcard__colors-data/meta DANS la card (mêmes noeuds que
 * l'ancien SSR → fallback QAP, _applyGlobalTopSwatches et color panel inchangés), puis
 * construit le même rang top-6 + badge "+N".
 */
const LazySwatchManager = {
    _observer: null,
    _queue: [],
    _active: 0,
    MAX_CONCURRENT: 6, // fetchs étalés (le WAF prod est sensible aux rafales)

    init(scope) {
        const cards = Array.from(scope || document.querySelectorAll('.mm-pcard')).filter(card => {
            const c = card.querySelector('[data-lazy-swatches]');
            return c && !c.dataset.swatchesLoaded;
        });
        if (!cards.length) return;
        if (!this._observer) {
            // rootMargin : précharge juste avant l'entrée réelle dans le viewport.
            this._observer = new IntersectionObserver((entries) => {
                for (const e of entries) {
                    if (!e.isIntersecting) continue;
                    this._observer.unobserve(e.target);
                    this._queue.push(e.target);
                }
                this._drain();
            }, { rootMargin: '200px 0px' });
        }
        cards.forEach(card => this._observer.observe(card));
    },

    _drain() {
        while (this._active < this.MAX_CONCURRENT && this._queue.length) {
            const card = this._queue.shift();
            this._active++;
            this._load(card)
                .catch(err => window.log('warn', '🎨 [LAZY_SWATCH]', 'Chargement pastilles échoué', { error: err.message }))
                .finally(() => { this._active--; this._drain(); });
        }
    },

    async _load(card) {
        const container = card.querySelector('[data-lazy-swatches]');
        const handle = card.dataset.wishlistHandle;
        if (!container || !handle || container.dataset.swatchesLoaded) return;
        container.dataset.swatchesLoaded = 'pending';
        try {
            // Timeout : un fetch suspendu ne doit jamais bloquer un slot de la file.
            const res = await fetch(`/products/${handle}?section_id=mm-pcard-colors&_v=${window.mmAssetsV || ''}`, {
                signal: AbortSignal.timeout(12000)
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            const dataEl = doc.querySelector('.mm-pcard__colors-data');
            const metaEl = doc.querySelector('.mm-pcard__colors-meta');
            if (!dataEl) throw new Error('colors-data absent de la réponse');

            // Injecter les JSON DANS la card : tout lecteur pcard.querySelector('.mm-pcard__colors-*')
            // (fallback QAP, chemin >250, color panel) fonctionne à l'identique de l'ancien SSR.
            const dataNode = document.importNode(dataEl, true);
            container.after(dataNode);
            if (metaEl) dataNode.after(document.importNode(metaEl, true));

            const colors = window.parseJSON(dataEl, []);
            const meta = metaEl ? window.parseJSON(metaEl, {}) : {};
            this._build(card, container, colors, meta);
            container.dataset.swatchesLoaded = 'true';

            // >250 variants : compléter avec les pages 2+ (chemin existant — il lit les JSON
            // injectés ci-dessus, exactement comme avec l'ancien SSR) ; il ré-applique aussi la
            // sélection du filtre couleur en fin de course.
            if (parseInt(card.dataset.variantsCount, 10) > 250) {
                ProductCardManager._applyGlobalTopSwatches(card).catch(err =>
                    window.log('warn', 'ProductCard', 'Global top swatches failed', { error: err.message }));
            } else {
                // Filtre couleur actif : updateCards a tourné avant que les pastilles n'existent —
                // appliquer la sélection sur le rang fraîchement construit.
                const stripHandles = ColorStripSync._activeHandles();
                if (stripHandles.length) {
                    const stripSet = new Set(stripHandles);
                    const match = ColorStripSync._findMatchingSwatch(card.querySelectorAll('.mm-pcard__swatch'), stripSet);
                    if (match) ColorStripSync._activateSwatch(card, match);
                    else ColorStripSync._injectFilteredSwatch(card, stripHandles);
                }
            }
        } catch (e) {
            delete container.dataset.swatchesLoaded;
            // La card a été désobservée au 1er intersect : sans ré-observation, un échec
            // transitoire (challenge anti-bot au chargement, réseau) la laissait définitivement
            // sans pastilles. Backoff + plafond pour ne pas boucler sur un échec permanent.
            const tries = (parseInt(container.dataset.swatchesRetries, 10) || 0) + 1;
            container.dataset.swatchesRetries = String(tries);
            // Backoff 5/10/15/20 s (fenêtre ~50 s) : le challenge anti-bot au chargement peut
            // durer >15 s — des retries trop rapprochés s'épuisaient tous DANS sa fenêtre et la
            // card restait définitivement sans rang de pastilles.
            if (tries <= 4 && this._observer) {
                setTimeout(() => this._observer.observe(card), 5000 * tries);
            }
            throw e;
        }
    },

    _build(card, container, colors, meta) {
        const MAX = 6;
        const media = card.querySelector('.mm-pcard__media');
        const noPreview = card.querySelector('.mm-pcard__no-preview');
        // Mêmes règles que le SSR : pastille = achetable ET pas "à la commande" ; le JSON de
        // l'endpoint est déjà trié stock-first.
        const top = colors.filter(c => c.available && !c.onDemand).slice(0, MAX);
        const nativeColor = container.dataset.nativeColor || '';
        // Une pastille active peut déjà exister (injectée par le filtre couleur AVANT le
        // chargement) : ne jamais la concurrencer avec l'activation native.
        const hasActive = !!container.querySelector('.mm-pcard__swatch--active');

        // Réutiliser les boutons déjà présents (pastille injectée par le filtre) — même
        // pattern que _applyGlobalTopSwatches.
        const existing = new Map();
        container.querySelectorAll('.mm-pcard__swatch').forEach(btn => existing.set(btn.getAttribute('title'), btn));

        const frag = document.createDocumentFragment();
        top.forEach(color => {
            let btn = existing.get(color.name);
            if (btn) {
                existing.delete(color.name); // conservé (état actif/injected intact)
            } else {
                btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'mm-pcard__swatch';
                if (color.style) btn.setAttribute('style', color.style);
                if (color.img) btn.dataset.swatchImg = color.img;
                // Groupe couleur : indispensable au match du filtre collection (titres traduits —
                // seul le group est stable multi-langue).
                if (color.group) btn.dataset.colorGroup = color.group;
                btn.dataset.available = String(!!color.available);
                btn.dataset.variantId = String(color.id);
                btn.title = color.name;
                btn.setAttribute('aria-label', color.name);
                CardSwatchManager.bindSwatch(btn, media, noPreview);
            }
            // Pastille active = sélection NATIVE Shopify (jamais la 1ère stock-first) ;
            // natif hors top-6 = aucune pastille active (même règle que le SSR).
            if (!hasActive && color.name === nativeColor) {
                btn.classList.add('mm-pcard__swatch--active');
            }
            frag.appendChild(btn);
        });
        // Boutons restants hors top-6 — sauf la pastille injectée par le filtre couleur.
        existing.forEach(btn => { if (!btn.dataset.injectedFilterSwatch) btn.remove(); });
        container.appendChild(frag);

        // Badge +N = couleurs ACHETABLES non montrées (même formule que le SSR).
        const remaining = Math.max(0, (meta.available ?? 0) - top.filter(c => c.available).length);
        if (remaining > 0 && !container.querySelector('.mm-pcard__swatch-more')) {
            const more = document.createElement('span');
            more.className = 'mm-pcard__swatch-more text-xs mm-black-text';
            more.textContent = `+${remaining}`;
            container.appendChild(more);
        }
    }
};

/*********************************************************************************************
 * 🖼️ PCARD IMAGE — crossfade géré en CSS, le JS ne fait que piloter les couches
 *
 * Structure : .mm-pcard__img--main (image 1) + .mm-pcard__img--hover (couche de crossfade).
 * - Survol card → :hover CSS fait apparaître --hover (image 2).
 * - Survol pastille couleur → previewInHover() injecte l'image du variant dans --hover
 *   puis ajoute .mm-pcard__media--show-hover → même crossfade qu'au survol de la card.
 * - clic pastille (lock) → setImage() change directement le src de --main.
 */
const PcardImageNav = {
    _getMainImg(media) {
        return media.querySelector('.mm-pcard__img--main');
    },

    _getHoverImg(media) {
        return media.querySelector('.mm-pcard__img--hover');
    },

    /* Survol pastille : crossfade vers l'image du variant.
       Double-buffer sur les 2 couches (--main / --hover) pour fondu fluide même
       en passant d'une pastille à une autre : on écrit la nouvelle image sur la
       couche actuellement cachée, puis on inverse les opacités. */
    previewInHover(media, src) {
        if (!media || !src) return;
        const mainImg = this._getMainImg(media);
        const hoverImg = this._getHoverImg(media);
        if (!mainImg || !hoverImg) return;
        this._ensureDefault(mainImg);
        this._ensureHoverDefault(hoverImg);
        clearTimeout(media._mmSettleTimer);

        /* Couche cachée = celle qui reçoit la nouvelle image */
        const showingHover = media.classList.contains('mm-pcard__media--show-hover');
        const incoming = showingHover ? mainImg : hoverImg;
        const sameSrc = incoming.src.split('?')[0] === src.split('?')[0];
        if (!sameSrc) {
            incoming.removeAttribute('srcset');
            incoming.removeAttribute('sizes');
            incoming.src = src;
        }
        /* Basculer : la couche entrante passe au-dessus */
        media.classList.toggle('mm-pcard__media--show-hover', incoming === hoverImg);
    },

    /* Fin du survol de la zone pastilles : crossfade retour vers l'image par défaut.
       On fait entrer le défaut sur la couche cachée puis on bascule (même mécanique
       que previewInHover) → fondu sans flash. Une fois la transition finie, on remet
       l'état repos canonique : --main = défaut visible, --hover = image 2 cachée
       (pour que le crossfade :hover de la card reparte de l'image 2). */
    clearPreview(media) {
        if (!media) return;
        const mainImg = this._getMainImg(media);
        const hoverImg = this._getHoverImg(media);
        if (!mainImg || !hoverImg) return;
        const def = mainImg.dataset.originalSrc || mainImg.dataset.defaultSrc;
        if (!def) { media.classList.remove('mm-pcard__media--show-hover'); return; }

        const showingHover = media.classList.contains('mm-pcard__media--show-hover');
        const incoming = showingHover ? mainImg : hoverImg;
        if (incoming.src.split('?')[0] !== def.split('?')[0]) {
            incoming.removeAttribute('srcset');
            incoming.removeAttribute('sizes');
            incoming.src = def;
        }
        media.classList.toggle('mm-pcard__media--show-hover', incoming === hoverImg);

        /* Re-canonisation après la transition (durée fixe, plus fiable que transitionend) */
        clearTimeout(media._mmSettleTimer);
        media._mmSettleTimer = setTimeout(() => {
            if (mainImg.src.split('?')[0] !== def.split('?')[0]) {
                mainImg.removeAttribute('srcset'); mainImg.removeAttribute('sizes');
                mainImg.src = def;
            }
            const hoverDef = hoverImg.dataset.defaultSrc;
            if (hoverDef && hoverImg.src.split('?')[0] !== hoverDef.split('?')[0]) {
                hoverImg.removeAttribute('srcset'); hoverImg.removeAttribute('sizes');
                hoverImg.src = hoverDef;
            }
            media.classList.remove('mm-pcard__media--show-hover');
        }, 500);
    },

    /* Mémorise l'image d'origine au 1er accès (avant tout swap) */
    _ensureDefault(mainImg) {
        if (mainImg && mainImg.dataset.defaultSrc === undefined) {
            mainImg.dataset.defaultSrc = mainImg.currentSrc || mainImg.src;
        }
    },

    _ensureHoverDefault(hoverImg) {
        if (hoverImg && hoverImg.dataset.defaultSrc === undefined) {
            hoverImg.dataset.defaultSrc = hoverImg.currentSrc || hoverImg.src;
        }
    },

    /* Change le src de l'image principale (img élément OU media conteneur accepté) */
    setImage(target, src) {
        if (!src) return;
        const media = target.matches?.(CARD_CONFIG.selectors.pcardMedia)
            ? target
            : target.closest(CARD_CONFIG.selectors.pcardMedia);
        if (!media) return;
        const mainImg = this._getMainImg(media);
        if (!mainImg) return;
        this._ensureDefault(mainImg);
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
        mainImg.src = src;
    },

    /* Compat : ancienne signature _setImage(img, src) utilisée par ColorPanel/QAP */
    _setImage(img, src) {
        this.setImage(img, src);
    },

    /* Restaure l'image verrouillée (clic swatch → originalSrc) ou l'image produit par défaut */
    reset(media) {
        const mainImg = this._getMainImg(media);
        if (!mainImg) return;
        const target = mainImg.dataset.originalSrc || mainImg.dataset.defaultSrc;
        if (target) {
            mainImg.removeAttribute('srcset');
            mainImg.removeAttribute('sizes');
            mainImg.src = target;
        }
    }
};

/*********************************************************************************************
 * 🎨 COLOR STRIP SYNC — met à jour les images des cards quand un chip couleur est sélectionné
 */
const ColorStripSync = {
    _handleize(str) {
        return (str || '').toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    },

    updateCards(handleList, isChecked) {
        const cards = document.querySelectorAll('.mm-pcard');
        if (!cards.length) return;

        // Décocher : reset uniquement si plus aucune couleur active.
        // Le test global est sorti de la boucle (était recalculé par card).
        if (!isChecked) {
            const anyActive = document.querySelector(
                '.mm-co-color-chip:checked, .mm-cf-color-swatch-checkbox:checked'
            ) !== null;
            if (anyActive) return;

            cards.forEach(card => {
                // Masquer le message "pas d'aperçu" (injecté pour un coloris filtré sans image).
                card.querySelector('.mm-pcard__no-preview')?.classList.remove('mm-pcard__no-preview--visible');
                const media = card.querySelector(CARD_CONFIG.selectors.pcardMedia) || card.querySelector('.mm-pcard__media');
                if (!media) return;
                const mainImg = PcardImageNav._getMainImg(media);
                if (mainImg) delete mainImg.dataset.originalSrc;
                media.classList.remove('mm-pcard__media--swatch-locked');
                PcardImageNav.reset(media);
                card.querySelectorAll('.mm-pcard__swatch').forEach((s, i) =>
                    s.classList.toggle('mm-pcard__swatch--active', i === 0)
                );
            });
            return;
        }

        // Cocher : lookup O(1) des handles sélectionnés (Set au lieu d'Array.includes en boucle).
        const handleSet = new Set(handleList);

        cards.forEach(card => {
            const swatches = card.querySelectorAll('.mm-pcard__swatch');
            const matched = this._findMatchingSwatch(swatches, handleSet);
            if (!matched) return;
            this._activateSwatch(card, matched, swatches);
        });
    },

    // Trouve le swatch d'une card dont le groupe ∈ handleSet, en préférant un coloris
    // achetable (data-available) ; fallback sur le 1er match OOS.
    _findMatchingSwatch(swatches, handleSet) {
        let matched = null;
        let fallback = null;
        for (const swatch of swatches) {
            const groupHandle = swatch.dataset.colorGroup || '';
            // Court-circuit : éviter _handleize(title) si le group matche déjà.
            if (!(handleSet.has(groupHandle) || handleSet.has(this._handleize(swatch.title)))) continue;
            if (swatch.dataset.available === 'true') return swatch;
            if (!fallback) fallback = swatch;
        }
        return matched || fallback;
    },

    // Active un swatch sur sa card : classe --active + verrouille l'image du média.
    _activateSwatch(card, matched, swatches) {
        // Fallback média : cards mono-image n'ont pas [data-pcard-images].
        const media = card.querySelector(CARD_CONFIG.selectors.pcardMedia) || card.querySelector('.mm-pcard__media');
        if (!media) return;

        swatches = swatches || card.querySelectorAll('.mm-pcard__swatch');
        swatches.forEach(s => s.classList.toggle('mm-pcard__swatch--active', s === matched));

        if (matched.dataset.swatchImg) {
            const mainImg = PcardImageNav._getMainImg(media);
            if (mainImg) {
                PcardImageNav._ensureDefault(mainImg);
                mainImg.dataset.originalSrc = matched.dataset.swatchImg;
            }
            PcardImageNav.setImage(media, matched.dataset.swatchImg);
            media.classList.toggle('mm-pcard__media--swatch-locked', matched !== swatches[0]);
        }
    },

    // Liste des handles de groupes couleur actuellement cochés (strip toolbar + drawer),
    // dans l'ordre du DOM (= priorité visuelle pour le choix multi-couleurs).
    _activeHandles() {
        const checkboxes = [
            ...document.querySelectorAll('.mm-co-color-chip:checked'),
            ...document.querySelectorAll('.mm-cf-color-swatch-checkbox:checked'),
        ];
        const handles = [];
        checkboxes.forEach(cb => {
            (cb.dataset.groupHandles || '').split('|').filter(Boolean).forEach(h => {
                if (!handles.includes(h)) handles.push(h);
            });
        });
        return handles;
    },

    // Re-apply current strip selection after AJAX DOM update
    restoreState() {
        const allHandles = this._activeHandles();
        if (!allHandles.length) return;
        this.updateCards(allHandles, true);
        this.ensureFilteredSwatches(allHandles);
    },

    // Cards dont la couleur filtrée est au-delà des 6 swatches rendus (cap grille) n'ont
    // aucun swatch matchant → on fetch l'endpoint allégé (toutes les couleurs + group),
    // on crée le swatch du groupe filtré et on l'injecte pour que l'image bascule.
    _filterCache: new Map(), // `${handle}` -> Promise<colors[] | null> (dédupe les fetchs concurrents)

    // L'image affichée n'est pas celle du groupe filtré (coloris introuvable, masqué ou sans
    // visuel) : afficher le message "visuel non disponible" plutôt que de laisser la card sur
    // l'image d'une autre couleur sans aucun signal.
    _markNoPreview(card) {
        card.querySelector('.mm-pcard__no-preview')?.classList.add('mm-pcard__no-preview--visible');
    },

    async ensureFilteredSwatches(activeHandles) {
        if (!activeHandles || !activeHandles.length) return;
        const handleSet = new Set(activeHandles);
        const cards = [...document.querySelectorAll('.mm-pcard')];

        // Par LOTS de 6, jamais tout d'un coup : 24 fetchs simultanés ici + la file lazy
        // (LazySwatchManager) = ~50 requêtes en rafale au chargement d'une page filtrée →
        // déclenche le challenge anti-bot, qui fait alors échouer TOUS les fetchs suivants.
        // 3 PASSES espacées : un fetch tombé pendant le challenge n'a sinon AUCUN retry
        // (_fetchFilterColors purge son cache d'erreur mais personne ne rappelait ce chemin)
        // → cards définitivement sans pastille filtrée. Les couleurs légitimement absentes
        // restent "missing" mais leur réponse est en cache → passes suivantes sans réseau.
        const BATCH = 6;
        for (let pass = 0; pass < 3; pass++) {
            const missing = cards.filter(card =>
                document.contains(card) &&
                !this._findMatchingSwatch(card.querySelectorAll('.mm-pcard__swatch'), handleSet)
            );
            if (!missing.length) return;
            for (let i = 0; i < missing.length; i += BATCH) {
                await Promise.allSettled(missing.slice(i, i + BATCH).map(card => this._injectFilteredSwatch(card, activeHandles)));
            }
            // Laisser retomber un éventuel challenge avant la passe suivante.
            if (pass < 2) await new Promise(r => setTimeout(r, 6000));
        }
    },

    async _injectFilteredSwatch(card, activeHandles) {
        const handle = card.dataset.wishlistHandle;
        if (!handle) return;

        let colors = await this._fetchFilterColors(handle);
        // Miss / erreur : card reste affichée sur son image par défaut → signaler que le visuel
        // ne correspond pas au coloris filtré.
        if (!colors) { this._markNoPreview(card); return; }

        // Produits >250 variants : l'endpoint filtre est cappé à 250 → une couleur des pages 2+
        // (ex noir Emma) serait introuvable et le filtre ne sélectionnerait ni pastille ni image.
        // Compléter avec les pages fetchées (cache partagé) et re-trier stock-first global pour
        // conserver la règle "coloris le plus stocké du groupe".
        const variantsCount = parseInt(card.dataset.variantsCount, 10);
        if (variantsCount > 250) {
            const extra = await window.mmFetchColorPages(handle, variantsCount);
            if (extra.length) {
                const known = new Set(colors.map(c => c.name));
                colors = colors.concat(extra.filter(c => !known.has(c.name)));
                colors.sort((a, b) => {
                    const sa = a.stock || 0, sb = b.stock || 0;
                    if ((sa > 0) !== (sb > 0)) return sa > 0 ? -1 : 1;
                    return sb - sa;
                });
            }
        }

        // Un swatch matchant a pu apparaître pendant le fetch (rebuild top-6 des produits >250,
        // re-render) : ne pas ré-injecter, mais S'ASSURER qu'il est actif — un return silencieux
        // laisserait la card sur son image par défaut (updateCards avait tourné avant que la
        // pastille n'existe).
        const activeSet = new Set(activeHandles);
        const existingMatch = this._findMatchingSwatch(card.querySelectorAll('.mm-pcard__swatch'), activeSet);
        if (existingMatch) {
            if (!existingMatch.classList.contains('mm-pcard__swatch--active')) {
                this._activateSwatch(card, existingMatch);
            }
            return;
        }

        // Couleur du groupe filtré à afficher. Priorité (couleurs déjà triées stock-first) :
        //   1. avec image + achetable   2. avec image (OOS)
        //   3. sans image + achetable   4. sans image (OOS)
        // Une couleur sans image est tout de même retenue : on injecte la pastille active et
        // on affiche le message "pas d'aperçu" (.mm-pcard__no-preview), comme au clic d'une
        // pastille sans image. Sinon la card resterait sur son image par défaut sans signal.
        let match = null;
        let withImgOOS = null;
        let noImgAvail = null;
        let noImgOOS = null;
        // Repli "à la commande" : hors filtre ces coloris n'ont jamais de pastille (règle grille
        // + PDP), mais le client a ICI demandé ce groupe explicitement et ils sont vendables
        // (fabrication à la demande). Les masquer afficherait une autre couleur que celle
        // filtrée. Retenus en DERNIER recours seulement : un coloris normalement disponible
        // reste prioritaire, quel que soit son rang stock-first.
        let onDemandWithImg = null;
        let onDemandNoImg = null;
        for (const c of colors) {
            if (!activeSet.has(c.group)) continue;
            if (c.onDemand) {
                if (c.img) { if (!onDemandWithImg) onDemandWithImg = c; }
                else if (!onDemandNoImg) onDemandNoImg = c;
                continue;
            }
            if (c.img) {
                if (c.available) { match = c; break; } // meilleur cas → stop
                if (!withImgOOS) withImgOOS = c;
            } else {
                if (c.available) { if (!noImgAvail) noImgAvail = c; }
                else if (!noImgOOS) noImgOOS = c;
            }
        }
        match = match || withImgOOS || noImgAvail || noImgOOS || onDemandWithImg || onDemandNoImg;
        // Aucun coloris du groupe filtré sur ce produit : l'image reste celle d'une autre
        // couleur → signaler au lieu de laisser la card mentir.
        if (!match) { this._markNoPreview(card); return; }

        // Construire le swatch et l'injecter avant le badge "+N".
        const swatchesWrap = card.querySelector('.mm-pcard__swatches');
        if (!swatchesWrap) { this._markNoPreview(card); return; }
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mm-pcard__swatch';
        if (match.style) btn.style.cssText = match.style;
        if (match.img) btn.dataset.swatchImg = match.img;
        if (match.group) btn.dataset.colorGroup = match.group;
        btn.dataset.available = String(!!match.available);
        if (match.id) btn.dataset.variantId = match.id;
        btn.title = match.name || '';
        btn.setAttribute('aria-label', match.name || '');
        btn.dataset.injectedFilterSwatch = 'true';
        // Pastille "à la commande" injectée par le filtre : marquée pour être distinguable
        // (elle n'existe que dans ce contexte, jamais dans la rangée SSR).
        if (match.onDemand) btn.dataset.onDemand = 'true';

        const more = swatchesWrap.querySelector('.mm-pcard__swatch-more');
        swatchesWrap.insertBefore(btn, more || null);

        // Coloris sans image : pastille active + message "pas d'aperçu", pas de swap d'image.
        const noPreview = card.querySelector('.mm-pcard__no-preview');
        if (!match.img) {
            card.querySelectorAll('.mm-pcard__swatch').forEach(s => s.classList.toggle('mm-pcard__swatch--active', s === btn));
            if (noPreview) noPreview.classList.add('mm-pcard__no-preview--visible');
            return;
        }
        if (noPreview) noPreview.classList.remove('mm-pcard__no-preview--visible');
        this._activateSwatch(card, btn);
    },

    _fetchFilterColors(handle) {
        // Cache de PROMESSE : dédupe les fetchs concurrents pour un même handle (restoreState
        // peut être appelé plusieurs fois avant que la 1ère réponse n'arrive).
        if (this._filterCache.has(handle)) return this._filterCache.get(handle);
        const promise = (async () => {
            try {
                const res = await fetch(`/products/${handle}?section_id=mm-pcard-colors-filter&_v=${window.mmAssetsV || ''}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
                return window.parseJSON(doc.querySelector('.mm-pcard__colors-data'), []);
            } catch (e) {
                window.log('warn', '🎨 [COLOR_STRIP]', 'Fetch couleurs filtre échoué', { handle, error: e.message });
                this._filterCache.delete(handle); // erreur : autoriser un retry ultérieur
                return null;
            }
        })();
        this._filterCache.set(handle, promise);
        return promise;
    },

    _initialized: false,

    init() {
        if (this._initialized) return;
        this._initialized = true;
        // updateCards = swap immédiat (swatches déjà dans le DOM). L'injection des couleurs
        // hors-cap (ensureFilteredSwatches) est déclenchée par restoreState() APRÈS le
        // re-render serveur — pas ici, sinon les 2 batches s'annulent via l'AbortController.
        document.addEventListener('collection:color-strip-changed', (e) => {
            const { handleList = [], isChecked = false } = e.detail || {};
            this.updateCards(handleList, isChecked);
        });
    }
};


/*********************************************************************************************
 * 🎬 EVENT DELEGATION & INITIALIZATION
 */
const ProductCardManager = {
    initialized: false,

    init() {
        // PERF : ne traiter QUE les cards pas encore initialisées (flag _mmCardInit).
        // init() est rappelé après chaque filtre ET chaque load-more ; sans ce garde,
        // load-more reparcourt toutes les cards déjà présentes (coût quadratique au scroll).
        // Filtre = re-render complet → cards neuves, toutes non flaggées (re-traitées, OK).
        const newCards = Array.from(document.querySelectorAll('.mm-pcard')).filter(c => !c._mmCardInit);

        // Initialize button states for quantity inputs (cards neuves uniquement)
        newCards.forEach(pcard => {
            pcard.querySelectorAll(CARD_CONFIG.selectors.quantityInput).forEach(input => {
                QuantityManager.updateButtonStates(input);
            });
        });

        // Setup swatch hover image swap (cards neuves uniquement)
        CardSwatchManager.setup(newCards);

        // Pastilles lazy : fetch à l'entrée dans le viewport (cards neuves uniquement)
        LazySwatchManager.init(newCards);

        // Init color strip sync (once — event delegation on document)
        ColorStripSync.init();

        // Quick Add Panel & Color Panel s'auto-initialisent (mm_quick_add.js / mm_color_panel.js)

        // Produits > 250 variants : le tri des pastilles ne voit que les 250 premiers.
        // Compléter en arrière-plan avec les pages 2+ et réordonner en top-6 stock GLOBAL.
        // Cards lazy pas encore chargées : SKIP — LazySwatchManager chaîne ce rebuild APRÈS
        // l'injection des JSON (sinon inline vide → merge construit depuis les seules pages 2+).
        newCards.forEach((pcard) => {
            const lazy = pcard.querySelector('[data-lazy-swatches]');
            if (lazy && lazy.dataset.swatchesLoaded !== 'true') return;
            if (parseInt(pcard.dataset.variantsCount, 10) > 250) {
                this._applyGlobalTopSwatches(pcard).catch(err =>
                    window.log('warn', 'ProductCard', 'Global top swatches failed', { error: err.message }));
            }
        });

        // Sync product links with active swatch variant (Wishlist Power detection)
        newCards.forEach((pcard) => {
            pcard._mmCardInit = true;
            const activeSwatch = pcard.querySelector('.mm-pcard__swatch--active[data-variant-id]');
            const variantId = activeSwatch?.dataset.variantId;
            if (!variantId) return;
            pcard.querySelectorAll('.mm-pcard__media-link, .mm-pcard__link').forEach((link) => {
                try {
                    const url = new URL(link.getAttribute('href'), window.location.origin);
                    if (!url.searchParams.get('variant')) {
                        url.searchParams.set('variant', variantId);
                        link.setAttribute('href', url.pathname + url.search);
                    }
                } catch (_) { /* ignore */ }
            });
        });

        // Card wishlist state (.mm-pcard--in-wishlist) is handled by WishlistManager (mm_wishlist.js)

        // Only add event listeners once (use event delegation on document)
        if (!this.initialized) {
            this.setupEventListeners();
            this.initialized = true;
        }

        window.log('info', 'ProductCard', 'Product card manager initialized', {
            formsCount: document.querySelectorAll(CARD_CONFIG.selectors.cardForm).length,
            newCardsCount: newCards.length
        });
    },

    /**
     * Card > 250 variants : réordonne les 6 pastilles en top-6 du stock GLOBAL.
     * Merge couleurs inline (6 SSR, champ stock = cumul des 250 premiers) + pages 2+
     * (mmFetchColorPages), tri stock décroissant. Une couleur page 1 hors des 6 SSR ne peut
     * pas entrer au top-6 (son stock < 6e du tri SSR) → le merge {6 SSR + pages 2+} suffit.
     * Boutons existants réutilisés (attrs SSR complets, hover déjà bindé) ; manquants créés
     * (couleurs > 250) ; hors top-6 retirés — pastille active native incluse (règle : natif
     * hors top-6 = aucune pastille active). Badge +N recalé sur les achetables globaux.
     */
    async _applyGlobalTopSwatches(pcard) {
        const MAX = 6;
        const handle = pcard.dataset.wishlistHandle || pcard.dataset.productHandle;
        const container = pcard.querySelector('.mm-pcard__swatches');
        if (!handle || !container) return;

        const inline = window.parseJSON(pcard.querySelector('.mm-pcard__colors-data'), []);
        const meta = window.parseJSON(pcard.querySelector('.mm-pcard__colors-meta'), {});
        const extra = await window.mmFetchColorPages(handle, parseInt(pcard.dataset.variantsCount, 10));
        if (!extra.length) return; // rien au-delà de 250 (ou fetch KO) : SSR déjà correct

        // Merge avec FUSION des couleurs à cheval sur la frontière des 250 : meta.p1 donne
        // {nom: [stock, available]} de TOUTES les couleurs des 250 premiers (l'inline n'en
        // résout que 6). Une couleur frontière cumule stock p1 + pages 2+ (sinon sous-classée
        // dans le tri global — le QAP la classerait différemment) et n'est comptée qu'une fois.
        // IMPORTANT : ne JAMAIS muter les objets de `extra` — ils viennent du cache partagé
        // mmFetchColorPages (même produit rendu sur 2 cards / réutilisé par le QAP) : une
        // mutation cumulerait les fusions à chaque exécution (15→17→19…). Clones locaux.
        const p1 = meta.p1 || {};
        const byName = new Map(inline.map(c => [c.name, c]));
        let extraAvailable = 0;
        const merged = inline.slice();
        for (const cExtra of extra) {
            const dup = byName.get(cExtra.name);
            if (dup) {
                dup.stock = (dup.stock || 0) + (cExtra.stock || 0);
                if (cExtra.available && !dup.available) { dup.available = true; extraAvailable++; }
                continue;
            }
            const c = { ...cExtra };
            const p1Entry = p1[c.name]; // couleur frontière hors des 6 inline : fusionner ses agrégats p1
            if (p1Entry) {
                c.stock = (c.stock || 0) + (p1Entry[0] || 0);
                if (c.available && !p1Entry[1]) extraAvailable++; // dispo p2 mais pas p1 : pas encore comptée
                else if (!c.available && p1Entry[1]) c.available = true;
            } else if (c.available) {
                extraAvailable++;
            }
            byName.set(c.name, c);
            merged.push(c);
        }
        merged.sort((a, b) => {
            const sa = a.stock || 0, sb = b.stock || 0;
            if ((sa > 0) !== (sb > 0)) return sa > 0 ? -1 : 1;
            return sb - sa; // sort stable → ordre SSR conservé à égalité
        });
        // Règle "couleur = axe primaire" (identique au SSR mm-pcard-colors-data L220) :
        // une couleur NON achetable et NON "à la commande" (OOS pure) ne devient jamais une
        // pastille. Le SSR inline la filtre déjà ; mais les couleurs des pages 2+ (extra,
        // mmFetchColorPages ne filtre rien) peuvent être OOS pures et, faute d'achetables en
        // nombre, remontaient dans le top-6 → pastilles fantômes absentes de la PDP.
        // Couleurs "à la commande" (onDemand) : gardées dans le +N (availableGlobal) mais
        // jamais en pastille (le color panel les affiche).
        const top = merged.filter(c => c.available && !c.onDemand).slice(0, MAX);

        // Boutons SSR par couleur (title = valeur d'option).
        const existing = new Map();
        container.querySelectorAll('.mm-pcard__swatch').forEach(btn => existing.set(btn.getAttribute('title'), btn));

        const media = pcard.querySelector('.mm-pcard__media');
        const noPreview = pcard.querySelector('.mm-pcard__no-preview');
        const moreEl = container.querySelector('.mm-pcard__swatch-more');

        const frag = document.createDocumentFragment();
        top.forEach(color => {
            let btn = existing.get(color.name);
            if (btn) {
                existing.delete(color.name); // conservé (attrs SSR + hover bindé)
            } else {
                btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'mm-pcard__swatch';
                if (color.style) btn.setAttribute('style', color.style);
                if (color.img) btn.dataset.swatchImg = color.img;
                // Groupe couleur : indispensable au match du filtre collection (les titres sont
                // traduits — "noir" ≠ handle "black" — seul le group est stable multi-langue).
                if (color.group) btn.dataset.colorGroup = color.group;
                btn.dataset.available = String(!!color.available);
                btn.dataset.variantId = String(color.id);
                btn.title = color.name;
                btn.setAttribute('aria-label', color.name);
                CardSwatchManager.bindSwatch(btn, media, noPreview);
            }
            frag.appendChild(btn);
        });
        // Hors top-6 global — sauf la pastille injectée par le filtre couleur (elle porte la
        // sélection active du filtre, la retirer ferait retomber la card sur l'image par défaut).
        existing.forEach(btn => { if (!btn.dataset.injectedFilterSwatch) btn.remove(); });
        container.insertBefore(frag, moreEl || null);

        // Badge +N = couleurs ACHETABLES globales non montrées (base unifiée avec le SSR).
        const availableGlobal = (meta.available ?? 0) + extraAvailable;
        const shownAvailable = top.filter(c => c.available).length;
        const remaining = Math.max(0, availableGlobal - shownAvailable);
        if (moreEl) {
            if (remaining > 0) moreEl.textContent = `+${remaining}`;
            else moreEl.remove();
        }

        // Filtre couleur actif : le rebuild du rang vient peut-être d'écraser la sélection
        // posée par updateCards/l'injection (course : updateCards tourne sur les pastilles SSR
        // qui n'ont pas la couleur >250, puis ce rebuild ajoute la bonne pastille — personne ne
        // l'active → la card retombe sur l'image par défaut après le re-render AJAX). On
        // ré-applique la sélection sur l'état final du rang.
        const stripHandles = ColorStripSync._activeHandles();
        if (stripHandles.length) {
            const stripSet = new Set(stripHandles);
            const match = ColorStripSync._findMatchingSwatch(pcard.querySelectorAll('.mm-pcard__swatch'), stripSet);
            if (match) ColorStripSync._activateSwatch(pcard, match);
            else ColorStripSync._injectFilteredSwatch(pcard, stripHandles);
        }
    },

    setupEventListeners() {
        // The wishlist coeur button (mode 'panel') opens the QAP via WishlistManager (mm_wishlist.js)

        // Event delegation for swatch click — lock image on card
        document.addEventListener('click', (e) => {
            const swatch = e.target.closest('.mm-pcard__swatch');
            if (!swatch) return;

            const pcard = swatch.closest('.mm-pcard');
            if (!pcard) return;

            const mainImg = pcard.querySelector('.mm-pcard__img--main');
            if (!mainImg) return;

            // Update active state
            const allSwatches = pcard.querySelectorAll('.mm-pcard__swatch');
            allSwatches.forEach(s => s.classList.remove('mm-pcard__swatch--active'));
            swatch.classList.add('mm-pcard__swatch--active');

            // Update variant ID in the form hidden input
            const variantId = swatch.dataset.variantId;
            if (variantId) {
                const variantInput = pcard.querySelector('[data-variant-id-input]');
                if (variantInput) variantInput.value = variantId;

                // Sync ?variant=<id> on product links (Wishlist Power detection)
                pcard.querySelectorAll('.mm-pcard__media-link, .mm-pcard__link').forEach((link) => {
                    try {
                        const url = new URL(link.getAttribute('href'), window.location.origin);
                        url.searchParams.set('variant', variantId);
                        link.setAttribute('href', url.pathname + url.search);
                    } catch (_) { /* ignore */ }
                });
            }

            // Message no-preview : visible uniquement si la couleur sélectionnée n'a pas d'image
            const hasImg = !!swatch.dataset.swatchImg;
            const noPreview = pcard.querySelector('.mm-pcard__no-preview');
            if (noPreview) noPreview.classList.toggle('mm-pcard__no-preview--visible', !hasImg);

            // Sans image : on s'arrête après la mise à jour de l'état actif + variant + message
            if (!hasImg) {
                window.log('info', 'ProductCard', 'Swatch clicked — no image', { variantId, value: swatch.dataset.value });
                return;
            }

            // Lock the image: update originalSrc so mouseleave sticks to this image
            const imgUrl = swatch.dataset.swatchImg;
            mainImg.dataset.originalSrc = imgUrl;
            mainImg.dataset.originalSrcset = '';
            mainImg.dataset.originalSizes = '';

            PcardImageNav._setImage(mainImg, imgUrl);

            // Désactiver le hover-image si ce n'est pas la première swatch
            const isFirstSwatch = swatch === allSwatches[0];
            pcard.querySelector('.mm-pcard__media')?.classList.toggle('mm-pcard__media--swatch-locked', !isFirstSwatch);

            window.log('info', 'ProductCard', 'Swatch clicked — image locked', { imgUrl, variantId, isFirstSwatch });
        });

        // Event delegation for quantity buttons (MUST be before stopPropagation listener)
        document.addEventListener('click', (e) => {
            // Handle decrease buttons
            const decreaseBtn = e.target.closest(CARD_CONFIG.selectors.quantityDecrease);
            if (decreaseBtn) {
                e.preventDefault();
                e.stopPropagation();
                window.log('info', 'ProductCard', 'Decrease button clicked', { button: decreaseBtn });
                QuantityManager.changeQuantity(-1, decreaseBtn);
                return;
            }

            // Handle increase buttons
            const increaseBtn = e.target.closest(CARD_CONFIG.selectors.quantityIncrease);
            if (increaseBtn) {
                e.preventDefault();
                e.stopPropagation();
                window.log('info', 'ProductCard', 'Increase button clicked', { button: increaseBtn });
                QuantityManager.changeQuantity(1, increaseBtn);
                return;
            }

            // Handle overlay toggle (mobile quick-add for products with variants)
            const toggleBtn = e.target.closest(CARD_CONFIG.selectors.toggleOverlay);
            if (toggleBtn) {
                e.preventDefault();
                e.stopPropagation();
                const card = toggleBtn.closest(CARD_CONFIG.selectors.productCard);
                if (!card) return;
                const overlay = card.querySelector(CARD_CONFIG.selectors.overlay);
                if (!overlay) return;

                const isActive = overlay.classList.toggle('mm-active');
                toggleBtn.style.display = isActive ? 'none' : '';
                window.log('info', 'ProductCard', 'Overlay toggled', { isActive });
                return;
            }
        });

        // Prevent clicks on variant/quantity selectors from triggering parent links
        document.addEventListener('click', (e) => {
            const clickedLink = e.target.closest('a');
            if (clickedLink) return;

            const variantLabel = e.target.closest('.mm-card-variant-option');
            if (variantLabel) {
                e.stopPropagation();
                return;
            }

            // Close any open mobile overlay when clicking outside
            if (!e.target.closest(CARD_CONFIG.selectors.overlay) && !e.target.closest(CARD_CONFIG.selectors.toggleOverlay)) {
                document.querySelectorAll(`${CARD_CONFIG.selectors.overlay}.mm-active`).forEach(overlay => {
                    overlay.classList.remove('mm-active');
                    const card = overlay.closest(CARD_CONFIG.selectors.productCard);
                    if (card) {
                        const toggleBtn = card.querySelector(CARD_CONFIG.selectors.toggleOverlay);
                        if (toggleBtn) toggleBtn.style.display = '';
                    }
                });
            }
        });

        // Event delegation for variant changes
        document.addEventListener('change', (e) => {
            const variantInput = e.target.closest(CARD_CONFIG.selectors.variantInput);
            if (variantInput) {
                const form = variantInput.closest(CARD_CONFIG.selectors.cardForm);
                if (form) {
                    // Update visual state
                    const optionName = variantInput.name;
                    const allInputsInGroup = form.querySelectorAll(`input[name="${optionName}"]`);

                    allInputsInGroup.forEach(input => {
                        const label = input.nextElementSibling;
                        if (label && label.tagName === 'LABEL') {
                            if (input.checked) {
                                label.classList.add('mm-selected');
                            } else {
                                label.classList.remove('mm-selected');
                            }
                        }
                    });

                    VariantManager.handleVariantChange(form);
                } else {
                    window.log('error', 'ProductCard', 'Form not found for variant input');
                }
            }
        });

        // Event delegation for quantity input changes (sanitize and validate)
        document.addEventListener('input', (e) => {
            const quantityInput = e.target.closest(CARD_CONFIG.selectors.quantityInput);
            if (quantityInput) {
                // Remove non-numeric characters first
                quantityInput.value = quantityInput.value.replace(/[^0-9]/g, '');

                // Then validate and clamp value
                QuantityManager.validateInput(quantityInput);
            }
        });
    }
};

/*********************************************************************************************
 * 🚀 AUTO-INIT
 */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        ProductCardManager.init();
        ColorStripSync.restoreState();
    });
} else {
    ProductCardManager.init();
    ColorStripSync.restoreState();
}

// Shopify sections: Reinitialize when sections are loaded/reloaded
document.addEventListener('shopify:section:load', (event) => {
    window.log('info', 'ProductCard', 'Shopify section loaded', {
        sectionId: event.detail?.sectionId
    });
    ProductCardManager.init();
});

// Shopify sections: Reinitialize when blocks are loaded
document.addEventListener('shopify:block:select', (event) => {
    window.log('info', 'ProductCard', 'Shopify block selected', {
        blockId: event.detail?.blockId
    });
    ProductCardManager.init();
});

// Export for external use
window.ProductCardManager = ProductCardManager;
window.ColorStripSync = ColorStripSync;
window.CardSwatchManager = CardSwatchManager;
window.PcardImageNav = PcardImageNav; // requis par mm_color_panel.js (_chooseColor)
